import { useCallback, useMemo, useState } from 'react';
import { FiCalendar, FiFileText } from 'react-icons/fi';
import { AccountTableCard, AnalyticsFiltersBar, MetricCard } from '../../components/analytics';
import { ANSWERS_COLUMNS, ANSWERS_FORMATTERS } from '../../components/analytics/accountTables';
import { DEFAULT_FILTERS, GUIA_METRICAS_URL, isQueryable, useAnalyticsMeta, useAnalyticsResource } from '../../components/analytics/analyticsPage';
import { formatInstantDayBR, formatInt } from '../../components/analytics/format';
import {
    buildMappingCards,
    CONTROLE_SERIES,
    CONTROLE_TABLE,
    controleRows,
    distributionTable,
    groupTable,
    SETOR_SERIES,
    SETOR_TABLE,
    setorBars,
    setorRows,
    type DistributionBar,
} from '../../components/analytics/mappingCards';
import { DistributionChart, GroupChart } from '../../components/analytics/SurveyCharts';
import type { MetricInfoKey } from '../../components/analytics/metricInfo';
import { analyticsApi, type AnalyticsFilters, type DistributionItem } from '../../services/analytics';
import { AdminLayout } from './AdminLayout';
import styles from './AdminAnalytics.module.css';
import biz from './AnalyticsBusiness.module.css';

const bars = (items: DistributionItem[]): DistributionBar[] => items.map((i) => ({ label: i.label, count: i.count, pct: i.pct }));

/** Mapeamento de clientes: perfil declarado na pesquisa de onboarding e comportamento por perfil. */
export function AdminCustomerMapping() {
    const [filters, setFilters] = useState<AnalyticsFilters>(DEFAULT_FILTERS);
    const [reloadKey, setReloadKey] = useState(0);
    const [onlyRespondents, setOnlyRespondents] = useState(false);
    const { meta, timeZone, today } = useAnalyticsMeta();
    const queryable = isQueryable(filters);
    const { data, loading, error } = useAnalyticsResource('mapping', analyticsApi.mapping, filters, reloadKey);

    const handleFiltersChange = useCallback((next: AnalyticsFilters) => setFilters(next), []);

    const view = useMemo(() => {
        if (!data) return null;
        const n = data.cards.responderam.count;
        const dist = (key: string, title: string, infoKey: MetricInfoKey, items: DistributionBar[], wide = false) => ({
            key,
            title,
            infoKey,
            items,
            wide,
            table: distributionTable(`mapeamento-${key}`, items),
        });
        return {
            n,
            cards: buildMappingCards(data),
            perfil: [
                dist('setor', 'Setor agrupado', 'setorAgrupado', setorBars(data)),
                dist('controle', 'Como controla as finanças hoje', 'comoControla', bars(data.comoControla)),
                dist('segmento', 'Segmento informado', 'segmentoInformado', bars(data.segmento), true),
                dist('faturamento', 'Faturamento anual declarado', 'faturamentoDeclarado', bars(data.faturamentoAnual)),
                dist('funcionarios', 'Número de funcionários', 'numeroFuncionarios', bars(data.numeroFuncionarios)),
            ],
            descoberta: [
                dist('objetivos', 'Objetivos declarados', 'objetivosDeclarados', bars(data.objetivos), true),
                dist('como-conheceu', 'Como conheceu', 'comoConheceu', bars(data.comoConheceu), true),
            ],
            setor: setorRows(data),
            controle: controleRows(data),
        };
    }, [data]);

    const respondentesLabel = view ? `${formatInt(view.n)} ${view.n === 1 ? 'respondente' : 'respondentes'} da safra` : '';

    return (
        <AdminLayout>
            <div className={styles.pageHeader}>
                <h2 className={styles.pageTitle}>Mapeamento de clientes</h2>
                <div className={styles.headerRight}>
                    <a className={styles.guideLink} href={GUIA_METRICAS_URL} target="_blank" rel="noopener noreferrer">
                        <FiFileText size={14} aria-hidden="true" />
                        Guia das métricas (PDF)
                    </a>
                    <span className={styles.today}>
                        <FiCalendar size={14} aria-hidden="true" />
                        {formatInstantDayBR(new Date().toISOString(), timeZone)}
                    </span>
                </div>
            </div>

            <AnalyticsFiltersBar
                filters={filters}
                onChange={handleFiltersChange}
                segmentos={meta?.filters.segmentos ?? []}
                faturamentos={meta?.filters.faturamentos ?? []}
                totalContas={data?.cards.contasCriadas ?? null}
                range={data ? { from: data.period.from, to: data.period.to } : null}
                maxDay={today}
            />

            <div className={styles.panel}>
                {!queryable && <p className={styles.notice}>Informe as duas datas do período personalizado.</p>}

                {error && (
                    <div className={styles.error} role="alert">
                        <span>{error}</span>
                        <button type="button" className={styles.retry} onClick={() => setReloadKey((k) => k + 1)}>
                            Tentar novamente
                        </button>
                    </div>
                )}

                {loading && !data && (
                    <div className={styles.loadingState} aria-live="polite">
                        Carregando...
                    </div>
                )}

                {view && (
                    <div className={`${styles.content} ${loading ? styles.contentLoading : ''}`} aria-busy={loading}>
                        <p className={biz.footnote}>
                            A safra são as empresas cadastradas no período escolhido; as respostas vêm da pesquisa de
                            onboarding.
                        </p>

                        <div className={biz.cardsGrid}>
                            {view.cards.map((c, i) => (
                                <MetricCard
                                    key={c.id}
                                    infoKey={c.id}
                                    title={c.title}
                                    value={c.value}
                                    caption={c.caption}
                                    highlight={c.highlight}
                                    tipAlign={i === 0 ? 'start' : 'end'}
                                />
                            ))}
                        </div>

                        <div className={`${styles.sectionHeader} ${styles.sectionGap}`}>
                            <h3 className={styles.sectionTitle}>Perfil do negócio</h3>
                            <p className={styles.sectionSubtitle}>{respondentesLabel}</p>
                        </div>
                        <div className={biz.chartsGrid}>
                            {view.perfil.map((d) => (
                                <DistributionChart
                                    key={d.key}
                                    title={d.title}
                                    infoKey={d.infoKey}
                                    bars={d.items}
                                    respondentes={view.n}
                                    table={d.table}
                                    exportFilename={`analytics-mapeamento-${d.key}.xlsx`}
                                    timeZone={timeZone}
                                    wide={d.wide}
                                />
                            ))}
                        </div>

                        <div className={`${styles.sectionHeader} ${styles.sectionGap}`}>
                            <h3 className={styles.sectionTitle}>Objetivos e descoberta</h3>
                            <p className={styles.sectionSubtitle}>{respondentesLabel}</p>
                        </div>
                        <div className={biz.chartsGrid}>
                            {view.descoberta.map((d) => (
                                <DistributionChart
                                    key={d.key}
                                    title={d.title}
                                    infoKey={d.infoKey}
                                    bars={d.items}
                                    respondentes={view.n}
                                    table={d.table}
                                    exportFilename={`analytics-mapeamento-${d.key}.xlsx`}
                                    timeZone={timeZone}
                                    wide={d.wide}
                                />
                            ))}
                        </div>

                        <div className={`${styles.sectionHeader} ${styles.sectionGap}`}>
                            <h3 className={styles.sectionTitle}>Comportamento por perfil</h3>
                            <p className={styles.sectionSubtitle}>{respondentesLabel}</p>
                        </div>
                        <div className={biz.chartsGrid}>
                            <GroupChart
                                title="Setor × comportamento"
                                infoKey="setorXComportamento"
                                series={SETOR_SERIES}
                                rows={view.setor}
                                respondentes={view.n}
                                table={groupTable('mapeamento-setor-comportamento', 'Setor', SETOR_TABLE, view.setor)}
                                exportFilename="analytics-mapeamento-setor-comportamento.xlsx"
                                timeZone={timeZone}
                            />
                            <GroupChart
                                title="Controle informado × ativação"
                                infoKey="controleXAtivacao"
                                series={CONTROLE_SERIES}
                                rows={view.controle}
                                respondentes={view.n}
                                table={groupTable('mapeamento-controle-ativacao', 'Controle informado', CONTROLE_TABLE, view.controle)}
                                exportFilename="analytics-mapeamento-controle-ativacao.xlsx"
                                timeZone={timeZone}
                            />
                        </div>
                    </div>
                )}

                <div className={styles.sectionGap}>
                    <AccountTableCard
                        name="respostas-por-cliente"
                        title="Respostas por cliente"
                        subtitle={data ? `${formatInt(data.cards.contasCriadas)} contas criadas no período` : undefined}
                        params={{ onlyRespondents: onlyRespondents ? 'true' : undefined }}
                        headerAction={
                            <label className={biz.toggle}>
                                <input
                                    type="checkbox"
                                    checked={onlyRespondents}
                                    onChange={(e) => setOnlyRespondents(e.target.checked)}
                                />
                                Somente respondentes
                            </label>
                        }
                        columns={ANSWERS_COLUMNS}
                        formatters={ANSWERS_FORMATTERS}
                        exportFilename="analytics-respostas-por-cliente.xlsx"
                        emptyMessage="Nenhuma conta criada no período."
                        filters={filters}
                        enabled={queryable}
                        refreshKey={reloadKey}
                        timeZone={timeZone}
                    />
                </div>
            </div>
        </AdminLayout>
    );
}
